import { Typography } from "@mui/material";
import { ColumnDef, Row } from "@tanstack/react-table";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutesEnum } from "@/routes/enum/Routes";
import { useIsMobile } from "@/hooks/use-mobile";

export type FoldersColumns = {
  idCliente: number;
  nombre: string;
  carpeta: string;
  nroDua: string;
  refCliente: string;
  canal: string;
  facturas: string[];
  facturasComerciales: string[];
  fechaNumerado: string;
};

const LinkableLabel = ({
  label,
  clienteId,
}: {
  label: string;
  clienteId: string;
}) => {
  const navigate = useNavigate();
  const { clienteNombre } = useLocation().state || {};
  return (
    <Typography
      fontFamily={"Inter"}
      fontSize={"14px"}
      fontWeight={400}
      className="truncate max-w-[140px] cursor-pointer underline text-[#1F6B45]"
      onClick={() => {
        navigate(RoutesEnum.DATA_FOLDERS, {
          state: {
            folderId: label,
            clienteNombre: clienteNombre,
            clienteId: clienteId,
          },
        });
      }}
    >
      {label}
    </Typography>
  );
};

const colorLabel = {
  V: {
    textColor: "text-green-500",
    label: "Verde",
    backgroundColor: "bg-green-50",
  },
  R: { textColor: "text-red-500", label: "Rojo", backgroundColor: "bg-red-50" },
  N: {
    textColor: "text-orange-500",
    label: "Naranja",
    backgroundColor: "bg-orange-50",
  },
  "": {
    textColor: "text-gray-500",
    label: "- - -",
    backgroundColor: "bg-gray-50",
  },
};

export function useFoldersColumns(): ColumnDef<FoldersColumns>[] {
  const isMobile = useIsMobile();
  const columnsFolders: ColumnDef<FoldersColumns>[] = [
    {
      accessorKey: "nombre",
      header: () => (
        <div className="text-left">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            Cliente
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<FoldersColumns> }) => {
        return (
          <div
            className="text-left font-medium max-w-[150px]"
            aria-label="cliente-nombre"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate max-w-[140px]"
            >
              {row.original.nombre}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorKey: "carpeta",
      header: () => (
        <div className="text-left">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            Carpeta
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<FoldersColumns> }) => {
        return (
          <div
            className="text-left font-medium max-w-[150px]"
            aria-label="numero-carpeta"
          >
            <LinkableLabel
              label={row.original.carpeta}
              clienteId={row.original.idCliente.toString()}
            />
          </div>
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: "nroDua",
      header: () => (
        <div className="text-left">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            DUA
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<FoldersColumns> }) => {
        return (
          <div
            className="text-left font-medium max-w-[150px]"
            aria-label="numero-dua"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {row.original.nroDua}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorKey: "refCliente",
      header: () => (
        <div className="text-left">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            {isMobile ? "Ref. cliente" : "Referencia cliente"}
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<FoldersColumns> }) => {
        return (
          <div
            className={`text-left font-medium ${
              isMobile ? "max-w-[100px]" : "max-w-[150px]"
            }`}
            aria-label="referencia-cliente"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate min-w-[140px]"
            >
              {row.original.refCliente ? row.original.refCliente : "- - -"}
            </Typography>
          </div>
        );
      },
    },
    !isMobile && {
      accessorKey: "canal",
      header: () => (
        <div className="text-center w-full flex items-center justify-center">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            Canal
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<FoldersColumns> }) => {
        return (
          <div
            className="text-left font-medium w-full flex items-center justify-center"
            aria-label="canal"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className={`truncate min-w-[140px] ${
                colorLabel[row.original.canal as keyof typeof colorLabel]
                  .textColor
              } ${
                colorLabel[row.original.canal as keyof typeof colorLabel]
                  .backgroundColor
              } rounded-md px-2 py-1 items-center flex justify-center`}
            >
              {row.original.canal
                ? colorLabel[row.original.canal as keyof typeof colorLabel]
                    .label
                : "- - -"}
            </Typography>
          </div>
        );
      },
    },
    !isMobile && {
      accessorKey: "nroFactura",
      header: () => (
        <div className="text-left">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            Facturas comerciales
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<FoldersColumns> }) => {
        return (
          <Tooltip>
            <TooltipTrigger>
              <div
                className="text-left font-medium max-w-[150px]"
                aria-label="numero-factura"
              >
                <Typography
                  fontFamily={"Inter"}
                  fontSize={"14px"}
                  fontWeight={400}
                  className="truncate min-w-[140px]"
                >
                  {row.original.facturasComerciales.length > 0
                    ? row.original.facturasComerciales
                        .map((factura) => factura)
                        .join(", ")
                    : "- - -"}
                </Typography>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <Typography
                fontFamily={"Inter"}
                fontSize={"14px"}
                fontWeight={400}
              >
                {row.original.facturasComerciales.length > 0
                  ? row.original.facturasComerciales
                      .map((factura) => factura)
                      .join(", ")
                  : "- - -"}
              </Typography>
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "fechaNumerado",
      header: () => (
        <div className="text-left">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            {isMobile ? "Fecha num." : "Fecha numerado"}
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<FoldersColumns> }) => {
        return (
          <div
            className="text-left font-medium max-w-[150px]"
            aria-label="fecha-numerado"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate min-w-[140px]"
            >
              {row.original.fechaNumerado
                ? row.original.fechaNumerado
                : "- - -"}
            </Typography>
          </div>
        );
      },
    },
  ].filter(Boolean) as ColumnDef<FoldersColumns>[];
  return columnsFolders;
}
